// Global authentication state management
export const checkAuthenticationStatus = () => {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  const authToken = localStorage.getItem('authToken');
  const timestamp = localStorage.getItem('authenticationTimestamp');
  
  // Check if either authentication system is valid
  if ((isAuthenticated && timestamp) || (isLoggedIn && authToken)) {
    // If using timestamp-based authentication, check if it's still valid (24 hours)
    if (isAuthenticated && timestamp) {
      const authTime = parseInt(timestamp);
      const currentTime = Date.now();
      const hoursDiff = (currentTime - authTime) / (1000 * 60 * 60);
      
      // If authentication is older than 24 hours, clear it
      if (hoursDiff > 24) {
        clearAuthentication();
        return false;
      }
    }
    
    return true;
  }
  
  return false;
};

// Check modal authentication status (separate from login authentication)
export const checkModalAuthenticationStatus = () => {
  // Get current selected person data
  const selectedPersonData = JSON.parse(localStorage.getItem('selectedPersonData') || '{}');
  const goinId = selectedPersonData.goinId;
  
  if (!goinId) {
    // Fallback to global authentication if no goinId
    const isModalAuthenticated = localStorage.getItem('modalAuthenticated') === 'true';
    const timestamp = localStorage.getItem('modalAuthenticationTimestamp');
    
    if (isModalAuthenticated && timestamp) {
      const authTime = parseInt(timestamp);
      const currentTime = Date.now();
      const hoursDiff = (currentTime - authTime) / (1000 * 60 * 60);
      
      if (hoursDiff > 2) {
        clearModalAuthentication();
        return false;
      }
      
      return true;
    }
    return false;
  }
  
  // Check authentication for specific deceased person
  const isModalAuthenticated = localStorage.getItem(`modalAuthenticated_${goinId}`) === 'true';
  const timestamp = localStorage.getItem(`modalAuthenticationTimestamp_${goinId}`);
  
  // Check if modal authentication is still valid (2 hours)
  if (isModalAuthenticated && timestamp) {
    const authTime = parseInt(timestamp);
    const currentTime = Date.now();
    const hoursDiff = (currentTime - authTime) / (1000 * 60 * 60);
    
    // If authentication is older than 2 hours, clear it
    if (hoursDiff > 2) {
      clearModalAuthenticationForGoinId(goinId);
      return false;
    }
    
    return true;
  }
  
  return false;
};

export const clearAuthentication = () => {
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('authenticatedCustomerName');
  localStorage.removeItem('authenticatedCustomerBirthday');
  localStorage.removeItem('authenticationTimestamp');
  
  // Also clear main login state
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('authToken');
  localStorage.removeItem('userInfo');
  
  // Also clear modal authentication (global)
  localStorage.removeItem('modalAuthenticated');
  localStorage.removeItem('modalAuthenticationTimestamp');
  
  // Clear all per-deceased-person modal authentication data
  clearAllModalAuthentications();
};

export const clearModalAuthentication = () => {
  localStorage.removeItem('modalAuthenticated');
  localStorage.removeItem('authenticatedCustomerName');
  localStorage.removeItem('authenticatedCustomerBirthday');
  localStorage.removeItem('modalAuthenticationTimestamp');
};

// Clear modal authentication for specific deceased person
export const clearModalAuthenticationForGoinId = (goinId) => {
  localStorage.removeItem(`modalAuthenticated_${goinId}`);
  localStorage.removeItem(`authenticatedCustomerName_${goinId}`);
  localStorage.removeItem(`authenticatedCustomerBirthday_${goinId}`);
  localStorage.removeItem(`modalAuthenticationTimestamp_${goinId}`);
};

// Clear all per-deceased-person modal authentication data
export const clearAllModalAuthentications = () => {
  // Get all localStorage keys
  const keys = Object.keys(localStorage);
  
  // Find and remove all modal authentication keys for deceased persons
  keys.forEach(key => {
    if (key.startsWith('modalAuthenticated_') || 
        key.startsWith('authenticatedCustomerName_') || 
        key.startsWith('authenticatedCustomerBirthday_') || 
        key.startsWith('modalAuthenticationTimestamp_')) {
      localStorage.removeItem(key);
    }
  });
  
  // Also clear the current authenticated goinId reference
  localStorage.removeItem('currentAuthenticatedGoinId');
};

export const getAuthenticatedCustomerName = () => {
  // Get current selected person data
  const selectedPersonData = JSON.parse(localStorage.getItem('selectedPersonData') || '{}');
  const goinId = selectedPersonData.goinId;
  
  if (goinId) {
    return localStorage.getItem(`authenticatedCustomerName_${goinId}`) || '';
  }
  
  // Fallback to global authentication
  return localStorage.getItem('authenticatedCustomerName') || '';
};

export const getAuthenticatedCustomerBirthday = () => {
  // Get current selected person data
  const selectedPersonData = JSON.parse(localStorage.getItem('selectedPersonData') || '{}');
  const goinId = selectedPersonData.goinId;
  
  if (goinId) {
    return localStorage.getItem(`authenticatedCustomerBirthday_${goinId}`) || '';
  }
  
  // Fallback to global authentication
  return localStorage.getItem('authenticatedCustomerBirthday') || '';
};

// Logout function - can be called from any component
export const logout = () => {
  clearAuthentication();
  // Optionally redirect to home page or show logout message
  window.location.reload(); // Refresh the page to reset all states
};

// Get authentication info for debugging
export const getAuthenticationInfo = () => {
  return {
    isAuthenticated: checkAuthenticationStatus(),
    customerName: getAuthenticatedCustomerName(),
    customerBirthday: getAuthenticatedCustomerBirthday(),
    timestamp: localStorage.getItem('authenticationTimestamp')
  };
};

// Check authentication and show alert if not logged in
export const requireAuthentication = () => {
  if (!checkAuthenticationStatus()) {
    alert('회원전용 서비스입니다. 로그인을 해주세요.');
    // Redirect to login page after alert
    window.location.href = '/login';
    return false;
  }
  return true;
};

// Check authentication and redirect to login if not authenticated
export const requireAuthenticationWithRedirect = () => {
  if (!checkAuthenticationStatus()) {
    alert('회원전용 서비스입니다. 로그인을 해주세요.');
    // Redirect to login page after alert
    window.location.href = '/login';
    return false;
  }
  return true;
};

// New functions for memorial plaza authentication
export const requireMemorialPlazaAuthentication = (currentPath) => {
  if (!checkAuthenticationStatus()) {
    // Store the current page for redirect after login
    localStorage.setItem('redirectAfterLogin', currentPath);
    return false;
  }
  return true;
};

// Check if user is logged in for memorial plaza pages
export const checkMemorialPlazaAccess = (currentPath) => {
  if (!checkAuthenticationStatus()) {
    // Store the current page for redirect after login
    localStorage.setItem('redirectAfterLogin', currentPath);
    return false;
  }
  return true;
};

// Get the redirect URL after login
export const getRedirectAfterLogin = () => {
  return localStorage.getItem('redirectAfterLogin') || '/';
};

// Clear the redirect URL after successful login
export const clearRedirectAfterLogin = () => {
  localStorage.removeItem('redirectAfterLogin');
};
